import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Bell, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { CompleteModal } from './Tasks/TaskModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const BELL_CONFIG = {
  lead: { color: 'emerald', label: 'Leads', icon: '🟢' },
  task: { color: 'blue', label: 'Tasks', icon: '🔵' },
};

function SingleBell({ type, className = '' }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ref = useRef(null);

  // Task modal state (only for task bell)
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(null);

  const conf = BELL_CONFIG[type];
  const colorMap = {
    emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', badge: 'bg-emerald-500', hoverBg: 'hover:bg-emerald-100', textTitle: 'text-emerald-800', textSub: 'text-emerald-600', dot: 'bg-emerald-400', bellStroke: 'text-emerald-600' },
    blue: { bg: 'bg-blue-50', ring: 'ring-blue-200', badge: 'bg-blue-500', hoverBg: 'hover:bg-blue-100', textTitle: 'text-blue-800', textSub: 'text-blue-600', dot: 'bg-blue-400', bellStroke: 'text-blue-600' },
  };
  const c = colorMap[conf.color];

  const fetchNotifs = useCallback(async (p = 1) => {
    try {
      // Only fetch unread notifications
      const r = await axios.get(`${BACKEND_URL}/api/notifications?notification_type=${type}&page=${p}&limit=10&unread_only=true`, getAuth());
      setNotifications(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(r.data.page || 1);
    } catch { /* silent */ }
  }, [type]);

  const pollCount = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/notifications/unread-count?notification_type=${type}`, getAuth());
      setUnread(r.data.unread_count || 0);
    } catch { /* silent */ }
  }, [type]);

  useEffect(() => { fetchNotifs(1); }, [fetchNotifs]);
  useEffect(() => { const iv = setInterval(pollCount, 20000); return () => clearInterval(iv); }, [pollCount]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target) && !showCompleteModal) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showCompleteModal]);

  const markAllRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/mark-read`, { mark_all: true, notification_type: type }, getAuth());
      setUnread(0);
      setNotifications([]); // Clear all — they're now read
    } catch { /* silent */ }
  };

  const markOneAndHandle = async (n) => {
    // Mark as read
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/mark-read`, { notification_id: n.id }, getAuth());
    } catch { /* silent */ }
    // Remove from list (read notifications disappear)
    setNotifications(prev => prev.filter(item => item.id !== n.id));
    setUnread(prev => Math.max(0, prev - 1));

    // For task notifications: open the task in CompleteModal
    if (type === 'task' && n.meta?.task_id) {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/tasks/${n.meta.task_id}`, getAuth());
        if (r.data) {
          setShowCompleteModal(r.data);
          setOpen(false);
        }
      } catch {
        // Task may have been deleted
      }
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const refreshAll = () => { fetchNotifs(1); };

  // Status badge for task notifications
  const getStatusBadge = (meta) => {
    if (!meta?.completion_status) return null;
    const map = {
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
      partial: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Partial' },
      not_completed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Not Done' },
    };
    const s = map[meta.completion_status];
    if (!s) return null;
    return <span className={`${s.bg} ${s.text} text-[9px] font-semibold px-1.5 py-0.5 rounded-full`}>{s.label}</span>;
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Bell Button */}
      <button
        data-testid={`bell-${type}`}
        onClick={() => { setOpen(!open); if (!open) fetchNotifs(1); }}
        className={`relative p-2 rounded-full transition-all ${c.bg} ${c.hoverBg} ring-1 ${c.ring}`}
        title={`${conf.label} Notifications`}
      >
        <Bell className={`w-5 h-5 ${c.bellStroke}`} />
        {unread > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] ${c.badge} text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-sm`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden" data-testid={`bell-${type}-panel`}>
          {/* Header */}
          <div className={`px-4 py-2.5 ${c.bg} border-b flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${c.textTitle}`}>{conf.label}</span>
              {unread > 0 && <span className={`text-[10px] ${c.badge} text-white px-1.5 py-0.5 rounded-full`}>{unread} new</span>}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid={`mark-all-read-${type}`}
                className={`text-[11px] ${c.textSub} hover:underline flex items-center gap-1`}>
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Notification List — only unread */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No unread notifications</div>
            ) : notifications.map(n => (
              <div key={n.id} onClick={() => markOneAndHandle(n)}
                className={`px-4 py-3 cursor-pointer transition-colors ${c.bg}/40 hover:bg-gray-50`}
                data-testid={`notif-item-${type}`}>
                {/* Row 1: Title + Status Badge + Time */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
                    <span className="text-xs font-semibold text-gray-800 truncate">
                      {n.title}
                    </span>
                    {type === 'task' && getStatusBadge(n.meta)}
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeAgo(n.created_at)}</span>
                </div>
                {/* Row 2: Message (task name) */}
                <p className="text-[11px] mt-0.5 text-gray-700 ml-4 line-clamp-1">
                  {n.message}
                </p>
                {/* Row 3: Remarks (if present, shown clearly) */}
                {n.meta?.remarks && (
                  <p className="text-[11px] mt-1 ml-4 px-2 py-1 bg-gray-100 rounded text-gray-600 italic line-clamp-2" data-testid="notif-remarks">
                    &ldquo;{n.meta.remarks}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="px-4 py-2 border-t flex items-center justify-between bg-gray-50">
              <span className="text-[10px] text-gray-400">Page {page}/{totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => fetchNotifs(page - 1)} disabled={page <= 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => fetchNotifs(page + 1)} disabled={page >= totalPages}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task CompleteModal — rendered via Portal to avoid relative parent constraint */}
      {type === 'task' && showCompleteModal && ReactDOM.createPortal(
        <CompleteModal
          showCompleteModal={showCompleteModal}
          setShowCompleteModal={setShowCompleteModal}
          setEditingTask={() => {}}
          setShowCreateModal={() => {}}
          refreshAll={refreshAll}
        />,
        document.body
      )}
    </div>
  );
}

export function DualNotificationBells() {
  return (
    <div className="flex items-center gap-2" data-testid="dual-bells">
      <SingleBell type="lead" />
      <SingleBell type="task" />
    </div>
  );
}

// Keep backward-compatible named export
export const NotificationBell = DualNotificationBells;
